/**
 * User-content translation (free APIs). Only calls API when text has changed since load (dirty).
 * Default: MyMemory (public, no signup).
 */

export type TranslationLocale = "en" | "fr";

const MYMEMORY_BASE = "https://api.mymemory.translated.net/get";
/** MyMemory allows max 500 bytes per request; use a safe chunk size for UTF-8. */
const MYMEMORY_MAX_CHARS = 400;

/** MyMemory response shape. */
interface MyMemoryResponse {
    responseData?: { translatedText?: string };
    responseStatus?: number;
}

/**
 * Translates one chunk via MyMemory GET (max ~500 bytes).
 */
async function translateChunkMyMemory(
    chunk: string,
    source: TranslationLocale,
    target: TranslationLocale
): Promise<string> {
    const params = new URLSearchParams({
        q: chunk,
        langpair: `${source}|${target}`,
    });
    const res = await fetch(`${MYMEMORY_BASE}?${params.toString()}`);
    if (!res.ok) {
        throw new Error(`Translation failed (${res.status}): ${res.statusText}`);
    }
    const data = (await res.json()) as MyMemoryResponse;
    const translated = data.responseData?.translatedText;
    if (translated == null) {
        throw new Error("Translation response missing translatedText");
    }
    return translated;
}

/**
 * Splits text into chunks of at most maxChars, trying to break on sentence or word boundaries.
 */
function chunkText(text: string, maxChars: number): string[] {
    if (text.length <= maxChars) return [text];
    const chunks: string[] = [];
    let remaining = text;
    while (remaining.length > 0) {
        if (remaining.length <= maxChars) {
            chunks.push(remaining);
            break;
        }
        const slice = remaining.slice(0, maxChars);
        const lastSpace = slice.lastIndexOf(" ");
        const lastNewline = slice.lastIndexOf("\n");
        const splitAt = Math.max(lastNewline, lastSpace, Math.floor(maxChars / 2));
        const chunk = splitAt > 0 ? slice.slice(0, splitAt) : slice;
        chunks.push(chunk);
        remaining = remaining.slice(chunk.length);
    }
    return chunks;
}

/**
 * Translates text using MyMemory (chunked if longer than limit).
 */
async function translateWithMyMemory(
    text: string,
    source: TranslationLocale,
    target: TranslationLocale
): Promise<string> {
    const chunks = chunkText(text, MYMEMORY_MAX_CHARS);
    const results = await Promise.all(
        chunks.map((chunk) => translateChunkMyMemory(chunk, source, target))
    );
    return results.join("");
}

/**
 * Translates text. Skips empty text.
 */
export async function translateText(
    text: string,
    source: TranslationLocale,
    target: TranslationLocale
): Promise<string> {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return trimmed;
    return translateWithMyMemory(trimmed, source, target);
}

/**
 * Fills target locale fields from source values: translates only when the source field is dirty,
 * otherwise uses the existing value from the entity. Minimizes API calls.
 */
export async function fillTranslatedFields<T extends Record<string, string | undefined>>(options: {
    pairs: { sourceKey: keyof T; targetKey: keyof T }[];
    values: T;
    dirtyFields: Partial<Record<keyof T, boolean>>;
    existing: Partial<T>;
    sourceLocale: TranslationLocale;
    targetLocale: TranslationLocale;
}): Promise<Partial<T>> {
    const { pairs, values, dirtyFields, existing, sourceLocale, targetLocale } = options;
    const result: Partial<T> = {};

    for (const { sourceKey, targetKey } of pairs) {
        const sourceValue = values[sourceKey];
        const isDirty = Boolean(dirtyFields[sourceKey]);
        const existingTarget = existing[targetKey];

        if (isDirty && sourceValue != null && String(sourceValue).trim()) {
            result[targetKey] = (await translateText(
                String(sourceValue),
                sourceLocale,
                targetLocale
            )) as T[keyof T];
        } else if (existingTarget != null) {
            result[targetKey] = existingTarget;
        }
    }

    return result;
}
